import React from 'react';
import { CheckCircle, GraduationCap, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SEO from '../SEO';

const CourseMatching: React.FC = () => {
    const navigate = useNavigate();

    const handleActionClick = () => {
        navigate('/eligibility?service=admissions');
    };

    return (
        <div className="bg-white min-h-screen pt-32 pb-20">
            <SEO
                title="Course Matching | Premium Education Advisory"
                description="Replace subjective consulting with data-driven precision. Our intelligent platform cross-references your profile against over 10,000 global programs."
                canonical="/admissions/course-matching"
            />

            <div className="bg-[#009B8F] text-white py-16 mb-16 relative overflow-hidden">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="max-w-7xl mx-auto px-6 relative z-10">
                    <span className="text-white/80 font-bold tracking-widest text-xs uppercase mb-4 block">Admissions Suite</span>
                    <h1 className="text-4xl md:text-5xl font-serif mb-4">Course Matching</h1>
                    <p className="text-xl text-white/90 max-w-2xl leading-relaxed">
                        Data-driven precision to find your mathematically optimal program matches.
                    </p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                    <div className="prose prose-lg text-slate-600 max-w-none">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="p-3 bg-[#009B8F]/10 rounded-full">
                                <GraduationCap className="text-[#009B8F] w-8 h-8" />
                            </div>
                            <h2 className="text-3xl font-serif text-[#011E26] m-0">The First Step to Global Success</h2>
                        </div>

                        <p className="leading-relaxed mb-6">
                            Choosing the right course is the first and most critical step in your international education.
                            With countless options available worldwide, relying on human guesswork or traditional
                            advising can lead to missed opportunities. At NexOverseas, we replace subjective consulting
                            with data-driven precision through our proprietary matching algorithm.
                        </p>

                        <p className="leading-relaxed mb-6">
                            Our intelligent platform begins by securely analyzing your academic background, test
                            scores, financial parameters, and career objectives. Instead of providing random or
                            commission-driven recommendations, the Nex-AI engine cross-references your specific
                            profile against a live database of over 10,000 global programs to find your mathematically
                            optimal matches.
                        </p>

                        <p className="leading-relaxed mb-6 border-l-4 border-[#009B8F] pl-6 italic bg-slate-50 py-4">
                            The system instantly evaluates complex variables across undergraduate, postgraduate, and
                            professional levels. It calculates exact eligibility requirements, historical acceptance rates,
                            and long-term career outcomes.
                        </p>

                        <p className="leading-relaxed mb-8">
                            We believe that the right technology unlocks global potential. Our platform ensures you
                            apply to programs that not only maximize your admission probability but also align directly
                            with international labor market trends.
                        </p>

                        <div className="bg-slate-50 p-8 rounded-sm border border-slate-100 mb-8">
                            <h3 className="text-lg font-bold text-[#011E26] mb-6 uppercase tracking-widest">Our Algorithmic Matching Features Include:</h3>
                            <ul className="space-y-4">
                                {[
                                    "Instant profile ingestion and data-driven career mapping.",
                                    "Automated course recommendations based on historical acceptance data.",
                                    "Real-time alignment with global skill shortages and post-study work trends.",
                                    "Instant, side-by-side comparative analytics of courses across multiple countries.",
                                    "Automated verification of course eligibility, compliance requirements, and projected ROI."
                                ].map((item, i) => (
                                    <li key={i} className="flex items-start gap-3 text-sm leading-relaxed">
                                        <CheckCircle className="w-5 h-5 text-[#009B8F] flex-shrink-0 mt-0.5" />
                                        <span className="font-medium text-slate-700">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <button
                            onClick={handleActionClick}
                            className="group inline-flex items-center gap-3 bg-[#009B8F] text-white px-8 py-4 font-bold uppercase tracking-widest text-xs hover:bg-[#011E26] transition-all duration-300 rounded-sm shadow-xl"
                        >
                            Run a check with AI <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                    <div className="space-y-8">
                        <div className="relative rounded-sm overflow-hidden shadow-2xl">
                            <img
                                src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&q=80&w=800"
                                alt="Students analyzing data"
                                className="w-full h-[400px] object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#011E26]/80 to-transparent flex items-end p-8">
                                <p className="text-white font-serif italic text-lg">"The precision of the Nex-AI matching engine saved me weeks of research."</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <img
                                src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&q=80&w=400"
                                alt="Strategic planning"
                                className="rounded-sm shadow-lg h-48 w-full object-cover"
                            />
                            <img
                                src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=400"
                                alt="Global education consultation"
                                className="rounded-sm shadow-lg h-48 w-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CourseMatching;
